import { useMemo } from "react";

import ExpandedCompanyRow from "./ExpandedCompanyRow";
import MarkIcon from "../../common/icon/MarkIcon";
import { LoadingSpinner, Table } from "frno-react";
import useApplicationStore from "../../../state/ApplicationStore";
import useDataStore from "../../../state/DataStore";
import usePensionProducts from "../../../hooks/usePensionProducts";
import useSavingsProfiles from "../../../hooks/useSavingsProfiles";
import computeFees from "../../../util/FeeComputationService";
import { CompanyPensionProduct } from "../../../types/CompanyPensionProduct";
import {
  PensionProductType,
  PensionProductTypeData,
} from "../../../data/PensionProductType";
import sort from "../../../util/SortService";
import useCompanyTableConfig from "./hooks/companyTableConfig";
import useTextStore from "../../../state/TextStore";

import "./table-company.scss";

type CompanyTableProps = {
  pensionProductType: PensionProductType;
};

function generateRowData(
  tableData: CompanyPensionProduct[],
  savingsValue: number,
  selectedCompany: string,
  getTextByKey: (key: string) => string,
) {
  return Object.values(tableData)
    .map((product) => ({
      ...product,
      computedFees: computeFees(product, savingsValue),
    }))
    .map((companyPensionProduct) => {
      return {
        rowId: companyPensionProduct.id,
        rowContent: [
          {
            columnKey: "company",
            cellData: {
              value: companyPensionProduct.company.name,
            },
          },
          {
            columnKey: "fund",
            cellData: {
              value: companyPensionProduct.securityName
                ? companyPensionProduct.securityName
                : companyPensionProduct.nameOfCheapestGlobalMutualFundOfferedInTheProduct,
            },
          },
          {
            columnKey: "global_index_fund",
            mobileHeaderValue: getTextByKey(
              "page.common.table.header.mobile.globalindexfund",
            ),
            cellData: {
              value:
                companyPensionProduct.isAtLeastOneGlobalIndexFundInTheProduct,
              icon: (
                <MarkIcon
                  isPositive={
                    companyPensionProduct.isAtLeastOneGlobalIndexFundInTheProduct
                  }
                />
              ),
            },
          },
          {
            columnKey: "total_fee",
            cellData: [
              {
                value: companyPensionProduct.computedFees.totalFee.amount,
                postfix: "kr",
              },
              {
                value: companyPensionProduct.computedFees.totalFee.percent,
                postfix: "%",
              },
            ],
          },
        ],

        expandedRowContent: (
          <ExpandedCompanyRow companyPensionProduct={companyPensionProduct} />
        ),
        highlightRow: companyPensionProduct.company.name === selectedCompany,
        defaultExpanded: companyPensionProduct.company.name === selectedCompany,
      };
    });
}

export default function CompanyTable({
  pensionProductType,
}: CompanyTableProps) {
  const { isLoading } = usePensionProducts(
    PensionProductTypeData[pensionProductType].apiId,
  );
  const { isLoading: isLoadingSavingsProfiles } = useSavingsProfiles(
    PensionProductTypeData[pensionProductType].apiId,
  );
  const { getTextByKey } = useTextStore();

  const { selectedCompany, savingsValue, tableAutoScroll, updateAutoScroll } =
    useApplicationStore();
  const { companyPensionProducts } = useDataStore();
  const { headers, sortableColumnsConfig } = useCompanyTableConfig();

  const tableContent = useMemo(() => {
    return generateRowData(
      companyPensionProducts,
      savingsValue,
      selectedCompany,
      getTextByKey,
    );
  }, [companyPensionProducts, savingsValue, selectedCompany, getTextByKey]);

  return !isLoading && !isLoadingSavingsProfiles ? (
    <Table
      className="table-company"
      headerContent={headers}
      bodyContent={tableContent}
      shouldAutoScroll={tableAutoScroll}
      onScrollComplete={() => updateAutoScroll(false)}
      sortConfig={{
        onSort: (rowData, orderBy) => sort(rowData, orderBy),
        sortableColumns: sortableColumnsConfig,
        defaultSortColumn: "total_fee",
        secondarySortColumn: "total_fee",
      }}
    />
  ) : (
    <LoadingSpinner />
  );
}
